import React from 'react'
import styled from 'styled-components'
import isEmpty from 'lodash/isEmpty'
import defaultTheme from '@ntuctech/devex-tangram/Theme/defaultTheme'
import FilterAndSort from '../../components/FilterAndSort'
import Text from '../../components/Text/Text'
import Layout from '../../components/Layout'
import { SCREEN_SIZE } from '../../lib/Media'
import { withRouter } from 'next/router'
import { modifyFormat, removeNullValues } from '../../lib/filterFormatter'
import { loadLayouts, getLayoutByName } from '../../components/LoadLayout'
import { PAGE_TYPES } from '../../lib/eventTrackerFunction'
import InfoIcon from '../../static/Info/InfoIcon'
import { EventTrackingConsumer } from '../EventTrackingProvider/EventTrackingProvider'
import { getConfigFor } from '../../utils/configService'
import { SPLIT_FEATURES } from '../../utils/constants'
import { ProductCollectionWithBannerList } from '../ProductCollectionWithBanner/ProductCollectionWithBanner'
import partition from 'lodash/partition'

const TAG_PAGE = 'tags'
const CATEGORY_PAGE = 'category'
const PRODUCT_LAYOUT_NAME = 'ProductCollection'
const CATEGORY_COLLECTION = 'CategoryCollection'

const HeadingAndCount = styled.div`
  margin-bottom: ${props => (props.showMargin ? '0.5rem' : '1.5rem')};
  h1,
  h1 span {
    font-size: 1.25rem;
    line-height: 1.75rem;
    margin: 0;
  }
  ${SCREEN_SIZE.From.Desktop} {
    h1,
    h1 span {
      font-size: 1.4375rem;
      line-height: 2rem;
    }
  }
`
const StyledLinkPointInfo = styled.h3`
  margin-top: 0.125rem;
  a {
    display: inline-block;
    vertical-align: middle;
    padding-left: 0.25rem;
  }
`

const SeoSection = styled.div`
  max-width: 43.75rem;
  margin: -1.3125rem 0 -0.5rem 0;
  ${SCREEN_SIZE.From.MobileL} {
    margin: -1.8125rem 0 0 0;
    p.last {
      margin-bottom: 0;
    }
  }
  ${SCREEN_SIZE.From.Desktop} {
    margin: 0.6875rem 0 1rem 0;
  }
  p,
  li {
    margin: 0.5rem 0 1.5rem 0;
    font-weight: normal;
    font-size: 0.875rem;
    color: rgb(51, 51, 51);
    line-height: 1.25rem;
  }
  li {
    margin: 0.5rem 0;
    &:last-child {
      margin-bottom: 1.5rem;
    }
  }
  a {
    text-decoration: none;
    color: ${defaultTheme.ColorBaseBrandPrimary500};
  }
  h2 {
    font-size: 1rem;
    line-height: 1.5rem;
    margin: 0px;
    font-weight: bold;
    color: rgb(51, 51, 51);
  }
`

const PlainDiv = styled.div`
  width: 100%; /* IE 11 */
  overflow: auto;
  display: block;
  ${SCREEN_SIZE.From.Desktop} {
    padding: 1rem 1.5rem 1.5rem;
    width: ${props =>
      props.withFilterAndSort ? 'calc(100% - 15.5rem)' : '100%'};
  }

  ${SCREEN_SIZE.From.UhdL} {
    width: 100%;
  }
`

const Container = styled.div`
  display: block;
  padding: 0;
  ${SCREEN_SIZE.From.Desktop} {
    display: flex;
  }
  ${SCREEN_SIZE.Below.Mobile} {
    margin: 0;
  }
`

const showProductCount = (layout, config) => {
  if (config.pageType === TAG_PAGE || config.pageType === CATEGORY_PAGE) {
    const filteredLayout =
      Array.isArray(layout) &&
      layout.filter(({ name, value }) => {
        let isValuePresent = false
        if (name === PRODUCT_LAYOUT_NAME) {
          isValuePresent =
            value && value.collection && value.collection.count > 0
        }
        if (name === CATEGORY_COLLECTION) {
          isValuePresent =
            value &&
            Array.isArray(value.collection) &&
            value.collection.length > 0
        }
        return (
          isValuePresent &&
          (name === PRODUCT_LAYOUT_NAME || name === CATEGORY_COLLECTION)
        )
      })
    return filteredLayout.length === 1
  }
  return true
}

class LayoutWithFilterAndSort extends React.Component {
  render() {
    const {
      organizationData,
      categoryList,
      title,
      layouts,
      config,
      ctaLocation,
      filterExcept,
      preLoadFirstImage,
      seoDataToShow,
      linkPointDisclaimerInfo = {},
    } = this.props
    // This function to fetch the stored cookie value for search variant
    const productLayout = getLayoutByName(
      layouts,
      PRODUCT_LAYOUT_NAME,
      config?.pageType
    )

    let displayCount = showProductCount(layouts, config)
    if (productLayout === undefined) {
      displayCount = false
    }

    let { filters, count } = (productLayout &&
      productLayout?.value?.collection) || { count: 0 }
    const isEnabledApiExperiment = true

    if (filters?.side) {
      filters = removeNullValues(filters?.side)
    } else {
      filters = modifyFormat(filters)
    }
    /**
     * even if one widget is present on page, we need to hide the filter & sort
     * NOTE: in future API should handle this flag & this code should be removed
     */
    let showSortAndFilter = !layouts.find(
      layout =>
        layout.data?.loadMoreType === 'SEEALL' &&
        layout.value?.title !== 'Recommended_Cat_Page' &&
        config?.pageType !== PAGE_TYPES.PROMO
    )

    // Override for Google Search feature flag
    const { configuration } = getConfigFor(SPLIT_FEATURES.PRDM_GOOGLE_SEARCH_ECOMMERCE)
    const isGoogleSearchVariant = configuration?.variantKey === 'gsc-b'
    const isSearchPage = config?.pageType === PAGE_TYPES.SEARCH

    if (isGoogleSearchVariant && isSearchPage) {
      showSortAndFilter = false;
    }

    const matchingProductCollection = layouts.find(
      layout => layout.name === 'ProductCollection'
    )
    const metaData = matchingProductCollection?.data?.metaData || {}

    const { isEnabled: swimlaneWithBannerEnabled } = getConfigFor(
      SPLIT_FEATURES.SWIMLANE_WITH_BANNER_ENABLED
    )
    const [
      productCollectionWithBannerLayouts,
      nonProductCollectionWithBannerLayouts,
    ] = partition(layouts, layout => {
      return layout.name === 'ProductCollectionWithBanner'
    })

    const isPromoPage = config?.pageType === PAGE_TYPES.PROMO
    return (
      <React.Fragment>
        <Layout
          organizationData={organizationData}
          noWrapper="fromDesktop"
          categoryList={categoryList}
          title={title}
        >
          {swimlaneWithBannerEnabled &&
            isPromoPage &&
            config?.pageType === PAGE_TYPES.PROMO &&
            !isEmpty(filters?.tag) && (
              <FilterAndSort
                tagCollection={filters?.tag}
                isEnabledApiExperiment={isEnabledApiExperiment}
                ctaLocation={config.ctaLocation}
              />
            )}
          {swimlaneWithBannerEnabled && (
            <ProductCollectionWithBannerList
              layouts={[...productCollectionWithBannerLayouts].slice(0, 2)}
              additionalConfig={{ ...config }}
              showSortAndFilter={null}
              additionalProps={{ title }}
              preLoadFirstImage
            />
          )}
          <Container>
            {showSortAndFilter && (
              <FilterAndSort
                metaData={metaData}
                filters={filters}
                filterExcept={filterExcept}
                query={SCREEN_SIZE.From.Desktop}
                isEnabledApiExperiment={isEnabledApiExperiment}
                ctaLocation={ctaLocation}
              />
            )}
            <PlainDiv
              withFilterAndSort={!isEmpty(filters) && showSortAndFilter}
            >
              <HeadingAndCount
                showMargin={config?.pageType !== PAGE_TYPES.SEARCH}
              >
                {title && (
                  <h1>
                    <Text
                      size="xl"
                      weight="bold"
                      color={defaultTheme.ColorBaseMineShaft700}
                    >
                      {title}
                    </Text>
                  </h1>
                )}
                {displayCount &&
                  config?.pageType !== PAGE_TYPES.PROMO &&
                  config?.pageType !== PAGE_TYPES.SEARCH && (
                    <Text
                      size="small"
                      weight="bold"
                      color={defaultTheme.ColorBaseMineShaft500}
                    >
                      {`${count} ${count > 1 ? 'products' : 'product'}`}
                    </Text>
                  )}
                {linkPointDisclaimerInfo?.faq_url &&
                  !linkPointDisclaimerInfo.eligible && (
                    <StyledLinkPointInfo>
                      <Text size="small" color="#333333">
                        Not eligible for LinkPoints and promo code
                      </Text>
                      <a
                        href={linkPointDisclaimerInfo.faq_url}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        <InfoIcon height="16px" width="16px" />
                      </a>
                    </StyledLinkPointInfo>
                  )}
              </HeadingAndCount>
              {!swimlaneWithBannerEnabled &&
                isPromoPage &&
                !isEmpty(filters?.tag) && (
                  <FilterAndSort
                    tagCollection={filters?.tag}
                    isEnabledApiExperiment={isEnabledApiExperiment}
                    ctaLocation={ctaLocation}
                  />
                )}
              {loadLayouts(
                nonProductCollectionWithBannerLayouts,
                { ...config },
                PRODUCT_LAYOUT_NAME,
                showSortAndFilter ? (
                  <>
                    <FilterAndSort
                      filters={filters}
                      filterExcept={filterExcept}
                      query={SCREEN_SIZE.Only.Mobile}
                      isEnabledApiExperiment={isEnabledApiExperiment}
                      ctaLocation={config.ctaLocation}
                    />
                    <FilterAndSort
                      filters={filters}
                      filterExcept={filterExcept}
                      query={SCREEN_SIZE.Only.Tablet}
                      isEnabledApiExperiment={isEnabledApiExperiment}
                      ctaLocation={config.ctaLocation}
                    />
                  </>
                ) : null,
                {
                  withFilterAndSort: !isEmpty(filters) && showSortAndFilter,
                  title: title,
                },
                preLoadFirstImage
              )}
              {seoDataToShow && (
                <SeoSection
                  size="s"
                  dangerouslySetInnerHTML={{
                    __html: seoDataToShow,
                  }}
                />
              )}
            </PlainDiv>
          </Container>
        </Layout>
      </React.Fragment>
    )
  }
}

const LayoutWithFilterAndSortWrapper = props => (
  <EventTrackingConsumer>
    {({ track }) => <LayoutWithFilterAndSort {...props} track={track} />}
  </EventTrackingConsumer>
)

export default withRouter(LayoutWithFilterAndSortWrapper)
